import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
// import axios from 'axios';
import axios from "../utils/axiosConfig";

import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";

const SalesDocumentStats = ({ fromFilter, toFilter, caseFilter, statusFilter, fromFilterCleared, billingPeriod }) => {
  const { t } = useTranslation(['common']);
  const [stats, setStats] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { salesDocuments } = useSelector((state) => state.salesDocumentList);
  const { salesPayments } = useSelector((state) => state.salesPaymentList);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { user } = useSelector((state) => state.userLoaded);
  
  // Extract stable IDs from filters to use as dependencies
  const fromId = useMemo(() => {
    return fromFilter && typeof fromFilter === 'object' && fromFilter._id 
      ? String(fromFilter._id) 
      : null;
  }, [fromFilter?._id]);
  
  const toId = useMemo(() => {
    return toFilter && typeof toFilter === 'object' && toFilter._id 
      ? String(toFilter._id) 
      : null;
  }, [toFilter?._id]);
  
  const caseId = useMemo(() => {
    return caseFilter && typeof caseFilter === 'object' && caseFilter._id 
      ? String(caseFilter._id) 
      : null;
  }, [caseFilter?._id]);
  
  // Wait for relationships to load before making initial API call
  // This prevents making a call before filters are auto-populated
  useEffect(() => {
    if (relationships && relationships.length > 0) {
      // Give a small delay to allow auto-populate to set filters
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 300); // Increased delay to ensure filters are set
      return () => clearTimeout(timer);
    } else if (relationships && relationships.length === 0) {
      // If relationships loaded but empty, proceed anyway
      setIsInitialLoad(false);
    }
  }, [relationships]);
  
  // Memoize billing period dates to prevent unnecessary re-renders
  const billingPeriodStart = useMemo(() => {
    return billingPeriod && billingPeriod[0] ? billingPeriod[0].toISOString() : null;
  }, [billingPeriod?.[0]?.getTime()]);
  
  const billingPeriodEnd = useMemo(() => {
    return billingPeriod && billingPeriod[1] ? billingPeriod[1].toISOString() : null;
  }, [billingPeriod?.[1]?.getTime()]);

  useEffect(() => {
    // Skip if still on initial load and we expect a filter to be auto-populated
    // Only wait if user exists, relationships are loaded, and no filter is set yet
    if (isInitialLoad && user && relationships?.length > 0 && !fromFilter && !fromFilterCleared) {
      return;
    }
    
    // If relationships haven't loaded yet, wait
    if (!relationships || relationships.length === 0) {
      return;
    }
    
    const fetchStats = async () => {
      try {
        // Build query parameters from filters
        const params = new URLSearchParams();
        if (fromId) {
          params.append('from', fromId);
        }
        if (toId) {
          params.append('to', toId);
        }
        if (caseId) {
          params.append('case', caseId);
        }
        if (statusFilter) {
          params.append('status', statusFilter);
        }
        if (billingPeriodStart && billingPeriodEnd) {
          params.append('startDate', billingPeriodStart);
          params.append('endDate', billingPeriodEnd);
        }
        
        const queryString = params.toString();
        const url = queryString 
          ? `/api/sales-documents/stats?${queryString}`
          : "/api/sales-documents/stats";
        
        const { data } = await axios.get(url);
        setStats(data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    fetchStats();
  }, [fromId, toId, caseId, statusFilter, isInitialLoad, user, relationships, fromFilter, billingPeriodStart, billingPeriodEnd]);

  if (!stats) {
    return (<Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px 24px' }}>
      <CircularProgress />
    </Box>);
  }

  const pieData = [
    { id: "Fees", label: t('common:fees'), value: stats.fees },
    { id: "Expenses", label: t('common:expenses'), value: stats.expenses },
    { id: "Provisions", label: t('common:provisions'), value: stats.provisions },
    { id: "VAT", label: t('common:vat'), value: stats.vat },
  ];

  const barData = [
    { category: t('common:paid'), value: stats.paid, color: "#FF6F61" }, // Coral
    { category: t('common:unpaid'), value: stats.unpaid, color: "#6B8E23" }, // Olive Green
    { category: t('common:credited'), value: stats.credited, color: "#FFD700" }, // Goldenrod
    { category: t('common:due_credit'), value: stats.dueCredit, color: "#40E0D0" }, // Turquoise
  ];

  const formatCurrency = (value) => `€ ${value.toFixed(2)}`;

  return (
    <Box sx={{ marginTop: 3 }}>
      {/* <Typography variant="h6" gutterBottom>
        Sales Document Stats
      </Typography> */}
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-around"
        alignItems="center"
        sx={{ marginTop: "-30px" }}
      >
        {/* Pie Chart */}
        <Box
          width="45%"
          height="50vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {pieData.some((item) => item.value > 0) ? (
            <ResponsivePie
              data={pieData.filter((item) => item.value > 0)} // Only show non-zero values
              margin={{ top: 40, right: 50, bottom: 40, left: 50 }}
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={3}
              colors={{ scheme: "pastel1" }}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.label}: ${formatCurrency(d.value)}`}
              enableArcLinkLabels={false}
              tooltip={({ datum: { label, value } }) => (
                <div
                  style={{
                    background: "white",
                    padding: "5px",
                    borderRadius: "5px",
                  }}
                >
                  <strong>{label}</strong>: {formatCurrency(value)}
                </div>
              )}
            />
          ) : (
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                padding: 2,
                backgroundColor: "background.paper",
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              {t('common:no_financial_data_available')}
            </Typography>
          )}
        </Box>

        {/* Bar Chart */}
        <Box width="45%" height="50vh">
          {" "}
          {/* Adjusted height */}
          <ResponsiveBar
            data={barData}
            keys={["value"]}
            indexBy="category"
            margin={{ top: 50, right: 50, bottom: 50, left: 80 }}
            padding={0.65}
            colors={({ data }) => data.color}
            valueFormat={(value) => formatCurrency(value)}
            axisBottom={{
              tickSize: 0,
              tickPadding: 5,
              legend: t('common:categories'),
              legendPosition: "middle",
              legendOffset: 32,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: t('common:amount_euro'),
              legendPosition: "middle",
              legendOffset: -60,
              format: (value) => `€ ${value}`,
            }}
            tooltip={({ id, value }) => (
              <div
                style={{
                  background: "white",
                  padding: "5px",
                  borderRadius: "5px",
                }}
              >
                <strong>{id}</strong>: {formatCurrency(value)}
              </div>
            )}
            theme={{
              tooltip: {
                container: {
                  background: "#fff",
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SalesDocumentStats;
